// apps/client/src/ui/install.tsx (full screen, 2026-10-15; spec §8.8) — how to play full screen, wherever the page is open: the title's
// tip (bottom right, over the town, never over Play now) and the settings entry. An iPhone or iPad gets "tap Share, then Add to Home
// Screen" (the home-screen app is the only full screen iOS gives a page); a browser that handed us its install prompt (Android, desktop
// Chrome) gets an Install button; the installed app gets no tip. The rules are boot/install.ts; the prompt is kept by boot/pwa.ts.
import { useState, useSyncExternalStore } from 'react';
import {
  canAddToHomeScreen, dismissInstallHint, installHintDismissed, installWay, isInstalled, showInstallHint, type FullscreenWindow, type InstallWay,
} from '../boot/install';
import { canPromptInstall, promptInstall, subscribeInstall } from '../boot/pwa';
import { Close, Install, Share } from './hud/Icons';
import { useT } from './i18n';
import { Button } from './kit';

const MODES = ['(display-mode: standalone)', '(display-mode: fullscreen)'];
function currentWay(): InstallWay {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'menu';
  const win = window as unknown as FullscreenWindow;
  const installed = typeof window.matchMedia === 'function' && isInstalled(win);
  return installWay({ installed, homeScreen: canAddToHomeScreen(navigator), canPrompt: canPromptInstall() });
}
function subscribe(fn: () => void): () => void {
  const offPrompt = subscribeInstall(fn);
  const queries = typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? MODES.map((q) => window.matchMedia(q)) : [];
  for (const q of queries) q.addEventListener?.('change', fn);
  document.addEventListener('fullscreenchange', fn);
  return () => {
    offPrompt();
    for (const q of queries) q.removeEventListener?.('change', fn);
    document.removeEventListener('fullscreenchange', fn);
  };
}
/** The way this page can go full screen, kept up to date as a prompt arrives or the app is installed. */
export function useInstallWay(): InstallWay {
  return useSyncExternalStore(subscribe, currentWay, () => 'menu');
}

const CSS = `
.install-hint { position: absolute; z-index: 2; right: max(16px, env(safe-area-inset-right)); bottom: max(14px, env(safe-area-inset-bottom));
  box-sizing: border-box; width: min(300px, 38cqw); display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 10px; align-items: start;
  background: var(--cream-50); border-radius: 18px; box-shadow: var(--sh-card); padding: 10px 8px 11px 10px; }
.install-pic { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 13px; background: var(--sky-100); color: var(--sky-700); }
.install-pic svg { width: 24px; height: 24px; }
.install-hint strong { display: block; font-weight: 900; font-size: 15px; line-height: 1.2; color: var(--wood-800); margin-top: 1px; }
.install-hint p { margin: 2px 0 0; font-weight: 700; font-size: 14px; line-height: 1.3; color: var(--wood-700); text-wrap: pretty; }
.install-hint .btn { margin-top: 8px; font-size: 15px; padding: 8px 16px 9px; }
.install-close { display: grid; place-items: center; width: 44px; height: 44px; margin: -7px -7px -7px 0; border: 0; border-radius: 50%; padding: 0;
  cursor: pointer; background: transparent; color: var(--wood-600); }   /* a 44 px finger target; the margins keep the card's layout as if it were 30 */
.install-close:hover { background: var(--cream-100); }
.install-close:focus-visible { outline: 3px solid var(--sky-700); outline-offset: 1px; }
.install-close svg { width: 18px; height: 18px; }
.install-row { display: flex; gap: 10px; align-items: center; }
.install-row p { margin: 0; }
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'install';
  el.textContent = CSS;
  document.head.appendChild(el);
}
const install = () => { void promptInstall().catch(() => false); };

/** The title's tip, shown until hidden (remembered on this device). */
export function InstallHint() {
  injectStyle();
  const t = useT();
  const way = useInstallWay();
  const [dismissed, setDismissed] = useState(installHintDismissed);
  if (!showInstallHint(way, dismissed)) return null;
  const hide = () => { dismissInstallHint(); setDismissed(true); };
  return (
    <aside className="install-hint" role="note" aria-labelledby="install-hint-h">
      <span className="install-pic">{way === 'ios' ? <Share /> : <Install />}</span>
      <div>
        <strong id="install-hint-h">{t('install.title')}</strong>
        {way === 'ios' ? <p>{t('install.iosSteps')}</p> : (
          <>
            <p>{t('install.promptBody')}</p>
            <button type="button" className="btn btn-primary" onClick={install}>{t('install.install')}</button>
          </>
        )}
      </div>
      <button type="button" className="install-close" aria-label={t('install.dismiss')} title={t('install.dismiss')} onClick={hide}><Close /></button>
    </aside>
  );
}

/** Settings → Play full screen: the same choice, always there (a hidden tip does not hide it). */
export function InstallSettings() {
  injectStyle();
  const t = useT();
  const way = useInstallWay();
  if (way === 'installed') return <p style={{ margin: 0 }}>{t('install.installed')}</p>;
  if (way === 'menu') return <p style={{ margin: 0 }}>{t('install.menu')}</p>;
  if (way === 'prompt') return (
    <>
      <p style={{ margin: 0 }}>{t('install.promptBody')}</p>
      <Button variant="quiet" onClick={install}>{t('install.installApp')}</Button>
    </>
  );
  return (
    <>
      <div className="install-row"><span className="install-pic"><Share /></span><p>{t('install.iosSteps')}</p></div>
      <p style={{ margin: 0 }}>{t('install.why')}</p>
    </>
  );
}
